import { useEffect, useState } from 'react';
import { useSafe } from '../context/SafeContext';
import { isBiometricUnlockSupported } from '../crypto/biometricUnlock';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { Icon } from './hbs/Icon';
import { EyeToggle } from './hbs/EyeToggle';

const DISMISS_KEY = 'hbs:biometric-cta-dismissed';
// Set when enrollment reports the device/browser can't do PRF (e.g. Chrome on
// macOS < 15, where a platform passkey routes to iCloud Keychain without PRF).
// Once set, we stop offering the nudge on this device — it can never succeed.
const UNSUPPORTED_KEY = 'hbs:biometric-unsupported';

function isDismissed(): boolean {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}
function setDismissed(): void {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
}
function isUnsupported(): boolean {
  try { return localStorage.getItem(UNSUPPORTED_KEY) === '1'; } catch { return false; }
}
function setUnsupported(): void {
  try { localStorage.setItem(UNSUPPORTED_KEY, '1'); } catch { /* ignore */ }
}

/** Cosmetic platform label for the biometric (mirrors BiometricUnlockSection). */
function biometricLabel(): string {
  const p = typeof navigator !== 'undefined' ? `${navigator.platform} ${navigator.userAgent}` : '';
  if (/Win/i.test(p)) return 'Windows Hello';
  if (/Mac|iPhone|iPad/i.test(p)) return 'Touch ID';
  return 'biometric unlock';
}

/**
 * A dismissible nudge on the safe list inviting the user to turn on biometric
 * unlock, shown only when the platform supports it, the user hasn't enrolled,
 * and they haven't dismissed the nudge. "Set up" expands an inline safe-password
 * prompt and binds the password behind the biometric via the same
 * `enableBiometricUnlock` path the Settings section uses; "Not now" dismisses it
 * for good. The full control stays in Settings (BiometricUnlockSection).
 */
export function BiometricUnlockCta(): JSX.Element | null {
  const { biometricEnrolled, enableBiometricUnlock } = useSafe();

  const [supported, setSupported] = useState<boolean | null>(null);
  const [dismissed, setDismissedState] = useState<boolean>(() => isDismissed() || isUnsupported());
  const [entering, setEntering] = useState(false);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const label = biometricLabel();

  useEffect(() => {
    let cancelled = false;
    void isBiometricUnlockSupported().then((ok) => { if (!cancelled) setSupported(ok); });
    return () => { cancelled = true; };
  }, []);

  // Hide once it's set up, unsupported, or dismissed.
  if (biometricEnrolled || dismissed || supported !== true) return null;

  async function handleEnable(): Promise<void> {
    setError('');
    setBusy(true);
    try {
      const result = await enableBiometricUnlock(password);
      setPassword('');
      if (result.ok) return; // biometricEnrolled flips true → this unmounts
      if (result.code === 'INVALID_PASSWORD') setError('Incorrect safe password.');
      else if (result.code === 'USER_CANCELED') setError(`${label} was cancelled.`);
      else if (result.code === 'NO_PRF') {
        // This device/browser can't do PRF — don't nag again after this.
        setUnsupported();
        setError('Your device or browser doesn’t support biometric unlock.');
      } else setError(`Couldn’t enable ${label}.`);
    } finally {
      setBusy(false);
    }
  }

  function handleNotNow(): void {
    setDismissed();
    setDismissedState(true);
  }

  return (
    <section
      className="mb-2 bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4"
      aria-label="Set up biometric unlock"
    >
      <div className="flex items-start gap-3">
        <Icon name="fingerprint" size={20} className="text-hbs-accent shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] text-hbs-text">Unlock faster with {label}</p>
          <p className="text-[13px] text-hbs-text-mute mt-0.5">
            Skip typing your safe password. It’s stored encrypted on this device, never on our servers.
          </p>

          {entering ? (
            <div className="flex flex-col gap-3 max-w-sm mt-3">
              <HbsInput
                label="Safe password"
                type={revealed ? 'text' : 'password'}
                value={password}
                onChange={setPassword}
                autoFocus
                mono={revealed}
                leadingIcon={<Icon name="key" size={16} className="text-hbs-accent" />}
                trailingActions={<EyeToggle revealed={revealed} onChange={setRevealed} />}
              />
              {error && <p className="text-[13px] text-hbs-danger" role="alert">{error}</p>}
              <div className="flex gap-2">
                <HbsButton
                  id="biometric-cta-confirm"
                  onClick={handleEnable}
                  loading={busy}
                  disabled={!password}
                >
                  Confirm
                </HbsButton>
                <HbsButton
                  variant="plain"
                  id="biometric-cta-cancel"
                  onClick={() => { setEntering(false); setPassword(''); setError(''); }}
                >
                  Cancel
                </HbsButton>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 mt-3">
              <HbsButton
                variant="subtle"
                id="biometric-cta-setup"
                onClick={() => { setError(''); setEntering(true); }}
              >
                Set up
              </HbsButton>
              <HbsButton variant="plain" id="biometric-cta-dismiss" onClick={handleNotNow}>
                Not now
              </HbsButton>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
